import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';

import type {
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import { parseMoneyInput } from '#components/product-finance/money';

import {
  actionStyle,
  checkboxStyle,
  Field,
  formatMoneyForInput,
  FormError,
  formStyle,
  getFinancialErrorMessage,
  helpStyle,
  inputStyle,
  StepCard,
} from './onboarding-shared';

export function SettingsStep({
  onboarding,
  client,
  onSaved,
}: Readonly<{
  onboarding: FinancialOnboardingState;
  client: ProductFinanceClient;
  onSaved: () => Promise<unknown>;
}>) {
  const { t } = useTranslation();
  const current = onboarding.settings;
  const [currency, setCurrency] = useState(current?.currency ?? 'GTQ');
  const [timezone, setTimezone] = useState(
    current?.timezone ?? getDefaultTimezone(),
  );
  const [hasBuffer, setHasBuffer] = useState(
    current?.minimumCashBuffer !== undefined,
  );
  const [buffer, setBuffer] = useState(
    current?.minimumCashBuffer === undefined
      ? ''
      : formatMoneyForInput(current.minimumCashBuffer.minorUnits),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timezones = getTimezones(timezone);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const parsedMinimumCashBuffer = hasBuffer
      ? parseMoneyInput(buffer)
      : undefined;
    if (parsedMinimumCashBuffer === null) {
      setError(t('Enter a positive minimum buffer.'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await client.configureFinancialSettings({
        currency,
        timezone,
        ...(parsedMinimumCashBuffer === undefined
          ? {}
          : { minimumCashBufferMinorUnits: parsedMinimumCashBuffer }),
      });
      await onSaved();
    } catch (submitError) {
      setError(getFinancialErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <StepCard title={<Trans>Choose your financial preferences</Trans>}>
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Currency</Trans>} htmlFor="finance-currency">
          <select
            id="finance-currency"
            value={currency}
            disabled={!onboarding.currencyChangeAllowed}
            onChange={event => setCurrency(event.target.value)}
            style={inputStyle}
          >
            <option value="GTQ">GTQ — Quetzal</option>
            {currency !== 'GTQ' && <option value={currency}>{currency}</option>}
          </select>
        </Field>
        {!onboarding.currencyChangeAllowed && (
          <p style={helpStyle}>
            <Trans>
              Currency cannot be changed after financial sources or liquid
              accounts exist. This protects existing amounts from being
              reinterpreted.
            </Trans>
          </p>
        )}

        <Field label={<Trans>Time zone</Trans>} htmlFor="finance-timezone">
          <select
            id="finance-timezone"
            value={timezone}
            onChange={event => setTimezone(event.target.value)}
            style={inputStyle}
          >
            {timezones.map(value => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
        <p style={helpStyle}>
          <Trans>
            Your time zone determines which local date is used by financial
            planning calculations.
          </Trans>
        </p>

        <div style={checkboxStyle}>
          <input
            id="finance-has-buffer"
            type="checkbox"
            aria-labelledby="finance-has-buffer-label"
            checked={hasBuffer}
            onChange={event => setHasBuffer(event.target.checked)}
          />
          <span id="finance-has-buffer-label">
            <Trans>Protect a minimum cash buffer</Trans>
          </span>
        </div>
        <p style={helpStyle}>
          <Trans>
            This is money you want to keep available and avoid spending. It is
            not a savings goal.
          </Trans>
        </p>
        {hasBuffer && (
          <Field
            label={<Trans>Minimum cash buffer</Trans>}
            htmlFor="finance-buffer"
          >
            <input
              id="finance-buffer"
              inputMode="decimal"
              value={buffer}
              onChange={event => setBuffer(event.target.value)}
              placeholder="1000.00"
              style={inputStyle}
            />
          </Field>
        )}

        <FormError message={error} />
        <ButtonWithLoading
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          isDisabled={isSubmitting}
          style={actionStyle}
        >
          <Trans>Save and continue</Trans>
        </ButtonWithLoading>
      </form>
    </StepCard>
  );
}

export function getDefaultTimezone(): string {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return detected.length === 0 ? 'America/Guatemala' : detected;
}

export function getTimezones(current: string): readonly string[] {
  const supportedValuesOf = (
    Intl as typeof Intl & {
      supportedValuesOf?: (key: 'timeZone') => string[];
    }
  ).supportedValuesOf;
  const values = supportedValuesOf?.('timeZone') ?? [
    'America/Guatemala',
    'America/Mexico_City',
    'America/New_York',
    'UTC',
  ];
  return values.includes(current) ? values : [current, ...values];
}
